import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { WORKSPACE_CHRONOLOGY, WORKSPACE_DOCUMENTS } from '../lib/workspace-data';
import { Stethoscope, FileText, CheckCircle2, AlertCircle, Download, Search, Filter, Plus, Edit2, Check, X, AlertTriangle, FileSearch, Maximize2, Save, Clock, GitMerge, Copy, ExternalLink } from 'lucide-react';
// Extend the mock data inline to support the new required columns for the prototype
const ENHANCED_CHRONOLOGY = WORKSPACE_CHRONOLOGY.map((event, index) => {
  const bodyPart = 'Lumbar Spine';
  let treatment = 'Evaluation';
  let medications = 'None';
  let confidence = 95;
  let reviewStatus = 'Pending';
  if (event.description.includes('Morphine')) {
    medications = 'Morphine 5mg IV';
    treatment = 'Pain Management';
  } else if (event.description.includes('MRI')) {
    treatment = 'MRI Scan';
  } else if (event.description.includes('PT') || event.description.includes('Therapy')) {
    treatment = 'Physical Therapy';
  } else if (event.description.includes('injection')) {
    treatment = 'Epidural Steroid Injection';
    medications = 'Steroid';
  } else if (event.description.includes('Oxycodone')) {
    medications = 'Oxycodone 5mg, Cyclobenzaprine 10mg';
    treatment = 'Prescription Refill';
  }
  if (event.hasAmbiguity) {
    confidence = 72;
    reviewStatus = 'Flagged';
  } else if (index % 3 === 0) {
    reviewStatus = 'Verified';
    confidence = 99;
  }
  return {
    ...event,
    bodyPart,
    treatment,
    medications,
    confidence,
    reviewStatus
  };
});
export function MedicalChronology() {
  const [selectedEventId, setSelectedEventId] = useState<string | null>(ENHANCED_CHRONOLOGY[1].id);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProvider, setFilterProvider] = useState('All'); // eslint-disable-line @typescript-eslint/no-unused-vars
  const selectedEvent = ENHANCED_CHRONOLOGY.find(e => e.id === selectedEventId);
  const sourceDoc = selectedEvent ? WORKSPACE_DOCUMENTS.find(d => d.id === selectedEvent.sourceDocId) : null;
  const filteredEvents = ENHANCED_CHRONOLOGY.filter(event => {
    const matchesSearch = event.description.toLowerCase().includes(searchQuery.toLowerCase()) || event.provider.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProvider = filterProvider === 'All' || event.provider.includes(filterProvider);
    return matchesSearch && matchesProvider;
  });
  const medicalDocs = WORKSPACE_DOCUMENTS.filter(d => d.type === 'Medical Record' || d.type === 'Billing');
  const verifiedCount = ENHANCED_CHRONOLOGY.filter(e => e.reviewStatus === 'Verified').length;
  const progressPercentage = Math.round(verifiedCount / ENHANCED_CHRONOLOGY.length * 100);
  return <div className="space-y-4 h-[calc(100vh-6rem)] flex flex-col overflow-hidden" data-id="element-1840">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shrink-0 bg-white p-4 rounded-lg border shadow-sm" data-id="element-1841">
        <div data-id="element-1842">
          <div className="flex items-center space-x-3" data-id="element-1843">
            <h1 className="text-xl font-bold tracking-tight flex items-center" data-id="element-1844">
              <Stethoscope className="w-5 h-5 mr-2 text-blue-600" data-id="element-1845" />
              Medical Chronology Workspace
            </h1>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-id="element-1846">

              Active Review
            </Badge>
          </div>
          <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground" data-id="element-1847">
            <span className="font-medium text-slate-900" data-id="element-1848">
              Ramirez v. Lone Star Freight
            </span>
            <span data-id="element-1849">•</span>
            <span data-id="element-1850">Reviewer: Sarah Jenkins</span>
            <span data-id="element-1851">•</span>
            <div className="flex items-center space-x-2" data-id="element-1852">
              <span data-id="element-1853">Progress: {progressPercentage}%</span>
              <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-1854">
                <div className="h-full bg-emerald-500 rounded-full" style={{
                width: `${progressPercentage}%`
              }} data-id="element-1855">
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex space-x-2" data-id="element-1856">
          <Button variant="outline" size="sm" data-id="element-1857">
            <Save className="w-4 h-4 mr-2" data-id="element-1858" />
            Save Draft
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-1859">

            <Download className="w-4 h-4 mr-2" data-id="element-1860" />
            Export XLSX
          </Button>
        </div>
      </div>

      {/* 3-Panel Workspace */}
      <div className="flex flex-col lg:flex-row gap-5 flex-1 min-h-0 overflow-hidden" data-id="element-1861">
        {/* LEFT PANEL: Source Documents (20%) */}
        <Card className="flex flex-col h-full lg:w-1/4 shrink-0 hidden xl:flex shadow-sm" data-id="element-1862">
          <CardHeader className="py-3 border-b shrink-0 bg-slate-50/50" data-id="element-1863">
            <CardTitle className="text-sm font-medium flex items-center justify-between" data-id="element-1864">
              <span data-id="element-1865">Source Records ({medicalDocs.length})</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-id="element-1866">
                <Plus className="w-4 h-4" data-id="element-1867" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-y-auto flex-1" data-id="element-1868">
            <div className="divide-y" data-id="element-1869">
              {medicalDocs.map(doc => <div key={doc.id} className="p-3 hover:bg-muted/30 transition-colors group cursor-pointer" data-id="element-1870">

                  <div className="flex items-start space-x-2" data-id="element-1871">
                    <FileText className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" data-id="element-1872" />
                    <div className="flex-1 min-w-0" data-id="element-1873">
                      <p className="text-xs font-medium truncate text-slate-900 group-hover:text-blue-600 transition-colors" title={doc.title} data-id="element-1874">

                        {doc.title}
                      </p>
                      <div className="flex items-center justify-between mt-1.5" data-id="element-1875">
                        <span className="text-[10px] text-muted-foreground font-mono" data-id="element-1876">
                          {doc.batesStart}
                        </span>
                        {doc.ocrQuality === 'Poor' ? <Badge variant="destructive" className="text-[8px] px-1 h-4" data-id="element-1877">

                            Low OCR
                          </Badge> : <Badge variant="outline" className="text-[8px] px-1 h-4 bg-emerald-50 text-emerald-700 border-emerald-200" data-id="element-1878">

                            Indexed
                          </Badge>}
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* CENTER PANEL: Chronology Table (50%) */}
        <Card className="flex flex-col h-full flex-1 min-w-0 overflow-hidden shadow-md border-slate-300" data-id="element-1879">
          <CardHeader className="py-3 border-b shrink-0 bg-white" data-id="element-1880">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-1881">
              <div className="flex items-center space-x-2 w-full sm:w-auto" data-id="element-1882">
                <div className="relative w-full sm:w-64" data-id="element-1883">
                  <Search className="w-4 h-4 absolute left-2.5 top-2 text-muted-foreground" data-id="element-1884" />
                  <input type="text" placeholder="Search events..." className="w-full h-8 pl-8 pr-3 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1885" />

                </div>
                <Button variant="outline" size="sm" className="h-8 px-2" data-id="element-1886">
                  <Filter className="w-4 h-4 mr-1" data-id="element-1887" /> Filter
                </Button>
              </div>
              <div className="flex items-center space-x-2 text-xs" data-id="element-1888">
                <span className="flex items-center text-amber-600 font-medium" data-id="element-1889">
                  <AlertTriangle className="w-3 h-3 mr-1" data-id="element-1890" /> 1 Conflict
                </span>
                <span className="text-slate-300" data-id="element-1891">|</span>
                <span className="flex items-center text-emerald-600 font-medium" data-id="element-1892">
                  <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-1893" /> {verifiedCount}{' '}
                  Verified
                </span>
              </div>
            </div>
          </CardHeader>
          <div className="flex-1 overflow-auto bg-slate-50" data-id="element-1894">
            <table className="w-full text-sm text-left border-collapse" data-id="element-1895">
              <thead className="text-xs text-slate-500 uppercase bg-white sticky top-0 shadow-sm z-10" data-id="element-1896">
                <tr data-id="element-1897">
                  <th className="px-3 py-2 font-medium w-24 border-b" data-id="element-1898">Date</th>
                  <th className="px-3 py-3 font-medium w-28 border-b" data-id="element-1899">
                    Provider
                  </th>
                  <th className="px-3 py-3 font-medium w-32 border-b" data-id="element-1900">
                    Event Type
                  </th>
                  <th className="px-3 py-3 font-medium border-b min-w-[200px]" data-id="element-1901">
                    Summary
                  </th>
                  <th className="px-3 py-3 font-medium w-36 border-b" data-id="element-1902">
                    Treatment
                  </th>
                  <th className="px-3 py-3 font-medium w-20 border-b text-center" data-id="element-1903">
                    Conf.
                  </th>
                  <th className="px-3 py-3 font-medium w-24 border-b text-center" data-id="element-1904">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white" data-id="element-1905">
                {filteredEvents.map(event => {
                const isSelected = event.id === selectedEventId;
                const isFlagged = event.reviewStatus === 'Flagged';
                const isVerified = event.reviewStatus === 'Verified';
                // Check for duplicate events (mock logic)
                const isDuplicate = event.id === 'c-4' || event.id === 'c-5';
                return <tr key={event.id} onClick={() => setSelectedEventId(event.id)} className={`
                        cursor-pointer transition-colors border-l-4
                        ${isSelected ? 'bg-blue-50/80 border-l-blue-500' : 'hover:bg-slate-50 border-l-transparent'}
                        ${isFlagged && !isSelected ? 'bg-amber-50/30 border-l-amber-400' : ''}
                        ${isDuplicate && !isSelected ? 'bg-slate-100/50 border-l-slate-300' : ''}
                      `} data-id="element-1906">

                      <td className="px-3 py-4 whitespace-nowrap font-medium text-slate-900" data-id="element-1907">
                        <div className="flex items-center" data-id="element-1908">
                          {event.date}
                          {isFlagged && <div data-id="element-1909"><AlertTriangle className="w-3 h-3 text-amber-500 inline ml-1" data-id="element-1910" /> Conflict Detected</div>}
                          {isDuplicate && <div data-id="element-1911"><Copy className="w-3 h-3 text-slate-400 inline ml-1" data-id="element-1912" /> Potential Duplicate Event</div>}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-slate-600" title={event.provider} data-id="element-1913">
                        <div className="line-clamp-2 leading-relaxed" data-id="element-1914">
                          {event.provider}
                        </div>
                      </td>
                      <td className="px-3 py-4" data-id="element-1915">
                        <Badge variant="secondary" className="text-[10px] font-normal bg-slate-100 text-slate-700" data-id="element-1916">

                          {event.eventType}
                        </Badge>
                      </td>
                      <td className="px-3 py-4 text-slate-700" data-id="element-1917">
                        <div className={`line-clamp-2 leading-relaxed ${isFlagged ? 'text-amber-900 font-medium' : ''} ${isDuplicate ? 'text-slate-500' : ''}`} data-id="element-1918">

                          {event.description}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-slate-600" title={event.treatment} data-id="element-1919">

                        {event.treatment}
                      </td>
                      <td className="px-3 py-4 text-center" data-id="element-1920">
                        <span className={`text-xs font-medium ${event.confidence > 90 ? 'text-emerald-600' : event.confidence > 75 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-1921">

                          {event.confidence}%
                        </span>
                      </td>
                      <td className="px-3 py-4 text-center" data-id="element-1922">
                        {isVerified ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" data-id="element-1923" /> : isFlagged ? <AlertCircle className="w-4 h-4 text-amber-500 mx-auto" data-id="element-1924" /> : <Clock className="w-4 h-4 text-slate-300 mx-auto" data-id="element-1925" />}
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* RIGHT PANEL: Event Details & Source (30%) */}
        <Card className="flex flex-col h-full lg:w-2/5 shrink-0 shadow-md border-slate-300 overflow-hidden" data-id="element-1926">
          {selectedEvent ? <>
              <CardHeader className="py-3 border-b shrink-0 bg-slate-50/50 flex flex-row items-center justify-between" data-id="element-1927">
                <CardTitle className="text-sm font-medium flex items-center" data-id="element-1928">
                  <Edit2 className="w-4 h-4 mr-2 text-blue-600" data-id="element-1929" />
                  Event Review
                </CardTitle>
                <div className="flex space-x-1" data-id="element-1930">
                  <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" data-id="element-1931">

                    <X className="w-3 h-3 mr-1" data-id="element-1932" /> Reject
                  </Button>
                  <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-1933">

                    <Check className="w-3 h-3 mr-1" data-id="element-1934" /> Verify
                  </Button>
                </div>
              </CardHeader>

              <div className="flex-1 overflow-y-auto" data-id="element-1935">
                {/* Ambiguity Alert */}
                {selectedEvent.hasAmbiguity && <div className="m-4 p-3 bg-amber-50 border border-amber-200 rounded-md" data-id="element-1936">
                    <div className="flex items-start" data-id="element-1937">
                      <GitMerge className="w-4 h-4 text-amber-600 mt-0.5 mr-2 shrink-0" data-id="element-1938" />
                      <div data-id="element-1939">
                        <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wider mb-1" data-id="element-1940">
                          Conflict Detected
                        </h4>
                        <p className="text-sm text-amber-800" data-id="element-1941">
                          {selectedEvent.ambiguityNote}
                        </p>
                      </div>
                    </div>
                  </div>}

                {/* Editable Fields Form */}
                <div className="p-4 space-y-4" data-id="element-1942">
                  <div className="grid grid-cols-2 gap-4" data-id="element-1943">
                    <div className="space-y-1.5" data-id="element-1944">
                      <label className="text-xs font-medium text-slate-500" data-id="element-1945">
                        Date
                      </label>
                      <input type="text" className="w-full text-sm p-2 border rounded-md bg-white focus:ring-2 focus:ring-blue-500 outline-none" defaultValue={selectedEvent.date} data-id="element-1946" />

                    </div>
                    <div className="space-y-1.5" data-id="element-1947">
                      <label className="text-xs font-medium text-slate-500" data-id="element-1948">
                        Event Type
                      </label>
                      <input type="text" className="w-full text-sm p-2 border rounded-md bg-white focus:ring-2 focus:ring-blue-500 outline-none" defaultValue={selectedEvent.eventType} data-id="element-1949" />

                    </div>
                  </div>

                  <div className="space-y-1.5" data-id="element-1950">
                    <label className="text-xs font-medium text-slate-500" data-id="element-1951">
                      Provider / Facility
                    </label>
                    <input type="text" className="w-full text-sm p-2 border rounded-md bg-white focus:ring-2 focus:ring-blue-500 outline-none" defaultValue={`${selectedEvent.provider} — ${selectedEvent.facility}`} data-id="element-1952" />

                  </div>

                  <div className="space-y-1.5" data-id="element-1953">
                    <label className="text-xs font-medium text-slate-500 flex justify-between" data-id="element-1954">
                      <span data-id="element-1955">Summary</span>
                      {selectedEvent.confidence < 80 && <span className="text-red-500 flex items-center" data-id="element-1956">
                          <AlertTriangle className="w-3 h-3 mr-1" data-id="element-1957" /> Low
                          Confidence
                        </span>}
                    </label>
                    <textarea className={`w-full text-sm p-2 border rounded-md bg-white focus:ring-2 focus:ring-blue-500 outline-none min-h-[160px] leading-relaxed resize-none ${selectedEvent.hasAmbiguity ? 'border-amber-300 bg-amber-50/30' : ''}`} defaultValue={selectedEvent.description} data-id="element-1958" />

                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-1959">
                    <div className="space-y-1.5" data-id="element-1960">
                      <label className="text-xs font-medium text-slate-500" data-id="element-1961">
                        Treatment
                      </label>
                      <input type="text" className="w-full text-sm p-2 border rounded-md bg-white focus:ring-2 focus:ring-blue-500 outline-none" defaultValue={selectedEvent.treatment} data-id="element-1962" />

                    </div>
                    <div className="space-y-1.5" data-id="element-1963">
                      <label className="text-xs font-medium text-slate-500" data-id="element-1964">
                        Medications
                      </label>
                      <input type="text" className="w-full text-sm p-2 border rounded-md bg-white focus:ring-2 focus:ring-blue-500 outline-none" defaultValue={selectedEvent.medications} data-id="element-1965" />

                    </div>
                  </div>
                </div>

                {/* Source Document Preview */}
                <div className="border-t border-slate-200 mt-2" data-id="element-1966">
                  <div className="p-3 bg-slate-50 border-b flex items-center justify-between" data-id="element-1967">
                    <div className="flex items-center space-x-2" data-id="element-1968">
                      <FileSearch className="w-4 h-4 text-slate-500" data-id="element-1969" />
                      <span className="text-xs font-medium text-slate-700" data-id="element-1970">
                        Source Comparison
                      </span>
                    </div>
                    <div className="flex space-x-2" data-id="element-1971">
                      <Badge variant="outline" className="text-[10px] bg-white font-mono" data-id="element-1972">

                        {selectedEvent.batesRef}
                      </Badge>
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0" data-id="element-1973">
                        <ExternalLink className="w-3 h-3" data-id="element-1974" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-100 min-h-[200px] relative" data-id="element-1975">
                    {/* Mock OCR Snippet */}
                    <div className="bg-white p-4 rounded border shadow-sm font-mono text-xs text-slate-700 leading-relaxed relative group" data-id="element-1976">
                      <Button variant="ghost" size="sm" className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1977">

                        <Maximize2 className="w-3 h-3" data-id="element-1978" />
                      </Button>
                      <p className="mb-2 text-slate-400" data-id="element-1979">
                        ...patient presented to clinic at 0900 hours.
                      </p>
                      <p className={selectedEvent.hasAmbiguity ? 'bg-amber-100 text-amber-900 p-1 rounded -mx-1' : 'bg-blue-50 text-blue-900 p-1 rounded -mx-1'} data-id="element-1980">

                        {selectedEvent.description}
                      </p>
                      <p className="mt-2 text-slate-400" data-id="element-1981">
                        Plan: Return to clinic in 2 weeks for follow up...
                      </p>
                    </div>

                    {sourceDoc && <div className="mt-3 flex items-center justify-between text-[10px] text-slate-500" data-id="element-1982">
                        <span className="truncate max-w-[200px]" title={sourceDoc.title} data-id="element-1983">

                          Doc: {sourceDoc.title}
                        </span>
                        <span data-id="element-1984">Conf: {selectedEvent.confidence}%</span>
                      </div>}
                  </div>
                </div>
              </div>
            </> : <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-6 text-center" data-id="element-1985">
              <FileSearch className="w-12 h-12 mb-4 text-slate-200" data-id="element-1986" />
              <p className="text-sm font-medium text-slate-600" data-id="element-1987">
                No Event Selected
              </p>
              <p className="text-xs mt-1" data-id="element-1988">
                Select an event from the chronology table to review details and
                compare against the source document.
              </p>
            </div>}
        </Card>
      </div>
    </div>;
}